import React, { useEffect, useRef, useState } from "react";
import L from "leaflet";

// Tile layer URLs — Esri Light Gray Canvas (base + labels) for street view, Esri World Imagery for satellite.
// Both are key-free. (CARTO Positron was dropped: it now watermarks tiles with "API KEY REQUIRED".)
const TILE_STREET     = "https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}";
const TILE_STREET_LBL = "https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Reference/MapServer/tile/{z}/{y}/{x}";
const TILE_SAT        = "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}";

function addTiles(map, view) {
  const layers = view === "satellite"
    ? [L.tileLayer(TILE_SAT, { maxZoom: 19 })]
    : [L.tileLayer(TILE_STREET, { maxZoom: 16 }), L.tileLayer(TILE_STREET_LBL, { maxZoom: 16 })];
  layers.forEach(l => l.addTo(map));
  return layers;
}

// Simple route map showing pickup & dropoff pins (no Google Maps key required)
const goldIcon = (label) => L.divIcon({
  className: "",
  html: `<div style="background:#D4AF37;color:#050505;font-family:'Outfit',sans-serif;font-weight:700;font-size:11px;padding:6px 9px;border-radius:50%;width:26px;height:26px;display:flex;align-items:center;justify-content:center;box-shadow:0 4px 12px rgba(212,175,55,0.5);border:2px solid #050505;">${label}</div>`,
  iconSize: [26, 26],
  iconAnchor: [13, 13],
});

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// Decode a Google encoded polyline → [[lat,lng], …]
function decodePolyline(str) {
  const pts = []; let index = 0, lat = 0, lng = 0;
  while (index < str.length) {
    let b, shift = 0, result = 0;
    do { b = str.charCodeAt(index++) - 63; result |= (b & 0x1f) << shift; shift += 5; } while (b >= 0x20);
    lat += (result & 1) ? ~(result >> 1) : (result >> 1);
    shift = 0; result = 0;
    do { b = str.charCodeAt(index++) - 63; result |= (b & 0x1f) << shift; shift += 5; } while (b >= 0x20);
    lng += (result & 1) ? ~(result >> 1) : (result >> 1);
    pts.push([lat / 1e5, lng / 1e5]);
  }
  return pts;
}

async function geocode(addr) {
  if (!addr) return null;
  // Google (via our backend, cached) first — reliable for business names like "Delta Hotels …"
  try {
    const r = await fetch(`${BACKEND}/api/geocode?address=${encodeURIComponent(addr)}`);
    if (r.ok) {
      const d = await r.json();
      if (d && d.lat != null && d.lng != null) return { lat: d.lat, lng: d.lng };
    }
  } catch (_e) { /* fall through */ }
  try {
    const r = await fetch(`https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(addr)}`, {
      headers: { "Accept-Language": "en" }
    });
    const data = await r.json();
    if (data && data[0]) return { lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) };
  } catch (_e) { /* ignore */ }
  return null;
}

export default function RouteMap({ pickup, dropoff, stops = [], routePolyline = [], height = 280 }) {
  const cleanStops = (stops || []).map(s => (s || "").trim()).filter(Boolean);
  const stopsKey = cleanStops.join("|");
  const polyKey = (routePolyline || []).join("|");
  const ref = useRef(null);
  const mapRef = useRef(null);
  const tileRef = useRef(null);
  const aliveRef = useRef(true);
  const [view, setView] = useState("street"); // "street" | "satellite"

  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
      const m = mapRef.current;
      mapRef.current = null;
      if (m) {
        try { m.off(); m.remove(); } catch (_e) { /* ignore */ }
      }
    };
  }, []);

  // Swap tile layer when view toggles
  useEffect(() => {
    if (!mapRef.current) return;
    try {
      (tileRef.current || []).forEach(l => mapRef.current.removeLayer(l));
      tileRef.current = addTiles(mapRef.current, view);
    } catch (_e) { /* ignore */ }
  }, [view]);

  useEffect(() => {
    if (!ref.current || !aliveRef.current) return;

    const run = async () => {
      // Initialize the map once
      if (!mapRef.current && ref.current && aliveRef.current) {
        try {
          mapRef.current = L.map(ref.current, { zoomControl: false, attributionControl: false }).setView([43.0389, -87.9065], 9);
          tileRef.current = addTiles(mapRef.current, view);
        } catch (_e) { return; }
      }
      const m = mapRef.current;
      if (!m || !aliveRef.current) return;

      // Clear previous markers/lines
      try {
        m.eachLayer(layer => {
          if (layer instanceof L.Marker || layer instanceof L.Polyline) m.removeLayer(layer);
        });
      } catch (_e) { return; }

      // Waypoints IN ORDER: A (pickup) → 1..n (stops) → B (drop-off)
      const points = [
        { addr: pickup, label: "A", testId: "route-pin-pickup" },
        ...cleanStops.map((addr, i) => ({ addr, label: String(i + 1), testId: `route-pin-stop-${i + 1}` })),
        ...(dropoff ? [{ addr: dropoff, label: "B", testId: "route-pin-dropoff" }] : []),
      ];
      const coords = await Promise.all(points.map(p => geocode(p.addr)));

      if (!aliveRef.current || !mapRef.current) return;
      const bounds = [];
      try {
        points.forEach((p, i) => {
          const c = coords[i];
          if (!c) return;
          const mk = L.marker([c.lat, c.lng], { icon: goldIcon(p.label), title: p.addr }).addTo(mapRef.current);
          if (mk.getElement()) mk.getElement().setAttribute("data-testid", p.testId);
          bounds.push([c.lat, c.lng]);
        });
        const road = (routePolyline || []).filter(Boolean).flatMap(decodePolyline);
        if (road.length > 1) {
          // Real road route from the pricing engine (same legs the fare is based on)
          L.polyline(road, { color: "#D4AF37", weight: 4, opacity: 0.9 }).addTo(mapRef.current);
          road.forEach(pt => bounds.push(pt));
        } else {
          const path = coords.filter(Boolean).map(c => [c.lat, c.lng]);
          if (path.length > 1) {
            L.polyline(path, { color: "#D4AF37", weight: 3, opacity: 0.85, dashArray: "8 8" }).addTo(mapRef.current);
          }
        }
        if (bounds.length) {
          mapRef.current.fitBounds(bounds, { padding: [40, 40], maxZoom: 12 });
        }
      } catch (_e) { /* unmounted mid-flight, ignore */ }
    };

    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickup, dropoff, stopsKey, polyKey]);

  return (
    <div data-testid="route-map-wrapper" style={{ position: "relative" }}>
      <div
        data-testid="route-map"
        ref={ref}
        style={{ height: `${height}px`, width: "100%", background: "#F5F5F5", border: "1px solid #E5E7EB", borderRadius: 4 }}
      />
      <div
        style={{
          position: "absolute",
          top: 10,
          right: 10,
          display: "flex",
          background: "rgba(255,255,255,0.96)",
          border: "1px solid #E5E7EB",
          borderRadius: 4,
          overflow: "hidden",
          boxShadow: "0 2px 8px rgba(0,0,0,0.10)",
          zIndex: 500,
          fontFamily: "Outfit, sans-serif",
        }}
      >
        <button
          type="button"
          data-testid="map-view-street"
          onClick={() => setView("street")}
          style={{
            padding: "6px 12px",
            fontSize: 11,
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            fontWeight: 600,
            background: view === "street" ? "#D4AF37" : "transparent",
            color: view === "street" ? "#0a0a0a" : "#52525B",
            border: 0,
            cursor: "pointer",
            transition: "all 0.15s ease",
          }}
        >Map</button>
        <button
          type="button"
          data-testid="map-view-satellite"
          onClick={() => setView("satellite")}
          style={{
            padding: "6px 12px",
            fontSize: 11,
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            fontWeight: 600,
            background: view === "satellite" ? "#D4AF37" : "transparent",
            color: view === "satellite" ? "#0a0a0a" : "#52525B",
            border: 0,
            borderLeft: "1px solid #E5E7EB",
            cursor: "pointer",
            transition: "all 0.15s ease",
          }}
        >Satellite</button>
      </div>
    </div>
  );
}
